import {
  AdjustmentsHorizontalIcon,
  ArrowLongDownIcon,
  ArrowLongUpIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ArrowTurnDownRightIcon,
  ArrowUpOnSquareStackIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  Bars3Icon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  CalculatorIcon,
  CalendarIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardIcon,
  ClockIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  CubeIcon,
  DocumentCurrencyDollarIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FolderArrowDownIcon,
  FunnelIcon,
  InboxIcon,
  InformationCircleIcon,
  KeyIcon,
  LinkIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  PercentBadgeIcon,
  PlayCircleIcon,
  PlusCircleIcon,
  ReceiptPercentIcon,
  RocketLaunchIcon,
  ShoppingCartIcon,
  SparklesIcon,
  SquaresPlusIcon,
  StarIcon,
  TagIcon,
  TrashIcon,
  TruckIcon,
  UserGroupIcon,
  UserIcon,
  UserMinusIcon,
  UserPlusIcon,
  ViewColumnsIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { FunctionComponent, PropsWithChildren, SVGProps } from 'react';
import { cn } from '../../../lib/styles';
import { CircleIcon } from './custom/circle';
import { DragHandleIcon } from './custom/drag-handle';
import type { IconName, IconProps, IconSize } from './types';

function MarginedIcon({ children }: PropsWithChildren) {
  return <span className="*:mx-0.5">{children}</span>;
}

const ICON_MAP: Record<IconName, FunctionComponent<SVGProps<SVGSVGElement>>> = {
  add: PlusCircleIcon,
  adjust: AdjustmentsHorizontalIcon,
  alertTriangle: ExclamationTriangleIcon,
  arrowDown: ArrowLongDownIcon,
  arrowRight: ArrowRightIcon,
  arrowUp: ArrowLongUpIcon,
  back: ChevronLeftIcon,
  bulkUpload: ArrowUpOnSquareStackIcon,
  calculator: CalculatorIcon,
  calendar: CalendarIcon,
  cart: ShoppingCartIcon,
  chat: ChatBubbleOvalLeftEllipsisIcon,
  check: CheckIcon,
  checkCircle: CheckCircleIcon,
  chevronDown: ChevronDownIcon,
  chevronRight: ChevronRightIcon,
  chevronsLeft: ChevronDoubleLeftIcon,
  chevronsRight: ChevronDoubleRightIcon,
  circle: CircleIcon,
  clock: ClockIcon,
  copy: ClipboardIcon,
  couponRequirement: ReceiptPercentIcon,
  cube: CubeIcon,
  delete: TrashIcon,
  document: DocumentTextIcon,
  download: FolderArrowDownIcon,
  dragHandle: DragHandleIcon,
  duplicate: DocumentDuplicateIcon,
  edit: PencilSquareIcon,
  ellipsis: EllipsisHorizontalIcon,
  exclamationCircle: ExclamationCircleIcon,
  externalLink: ArrowTopRightOnSquareIcon,
  filter: FunnelIcon,
  inbox: InboxIcon,
  infoCircle: InformationCircleIcon,
  inherit: ArrowTurnDownRightIcon,
  key: KeyIcon,
  link: LinkIcon,
  lock: LockClosedIcon,
  mapPin: MapPinIcon,
  menu: Bars3Icon,
  minusCircle: MinusCircleIcon,
  money: BanknotesIcon,
  panelCollapse: ArrowsPointingInIcon,
  panelExpand: ArrowsPointingOutIcon,
  percentage: PercentBadgeIcon,
  payment: CreditCardIcon,
  playCircle: PlayCircleIcon,
  plusCircle: PlusCircleIcon,
  premium: StarIcon,
  priceList: DocumentCurrencyDollarIcon,
  refresh: ArrowPathIcon,
  rocket: RocketLaunchIcon,
  search: MagnifyingGlassIcon,
  settings: Cog6ToothIcon,
  shipping: MapPinIcon,
  sort: ArrowsUpDownIcon,
  store: BuildingStorefrontIcon,
  tag: TagIcon,
  upgrade: SparklesIcon,
  upload: ArrowUpTrayIcon,
  user: UserIcon,
  userAdd: UserPlusIcon,
  userRemove: UserMinusIcon,
  userGroup: UserGroupIcon,
  change: ArrowsUpDownIcon,
  squaresPlus: SquaresPlusIcon,
  buildingOffice: BuildingOfficeIcon,
  truck: TruckIcon,
  view: EyeIcon,
  viewColumns: ViewColumnsIcon,
  x: XMarkIcon,
  xCircle: XCircleIcon,
  increase: (props) => (
    <MarginedIcon>
      <ArrowTrendingUpIcon {...props} />
    </MarginedIcon>
  ),
  decrease: (props) => (
    <MarginedIcon>
      <ArrowTrendingDownIcon {...props} />
    </MarginedIcon>
  ),
} as const;

const SIZE_MAP: Record<IconSize, string> = {
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-4.5',
  lg: 'size-5',
  xl: 'size-6',
};

export function Icon({ name, ariaLabel, size = 'sm' }: IconProps) {
  if (!name) {
    return null;
  }

  const IconSource = ICON_MAP[name];
  if (!IconSource) {
    console.error(`[Icon] Missing icon mapping for "${name}"`);
    return null;
  }

  return (
    <span className="shrink-0" aria-label={ariaLabel} role="img" aria-hidden={!ariaLabel}>
      <IconSource aria-hidden className={cn(SIZE_MAP[size], 'stroke-2')} />
    </span>
  );
}
